/**
 * sub-category-advanced.js
 *
 * Uses Swiper.js for the scrollable track (same as the base sub-category section).
 * Swiper handles: slide navigation, drag, touch, pagination dots with active class.
 * Custom logic handles: YouTube/PDF modal popups, sticky width sync, auto-scroll mode.
 */

;(() => {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────────── */
  const isMobile = () => window.innerWidth < 768;

  function getYouTubeId(el) {
    return isMobile()
      ? (el.dataset.ytMobile || el.dataset.ytDesktop || '')
      : (el.dataset.ytDesktop || el.dataset.ytMobile || '');
  }

  /* ── Modal ───────────────────────────────────────────────────── */
  class MediaModal {
    constructor(sectionEl) {
      this.modal    = sectionEl.querySelector('.sub-cat-adv__modal');
      this.content  = this.modal && this.modal.querySelector('.sub-cat-adv__modal-content');
      this.closeBtn = this.modal && this.modal.querySelector('.sub-cat-adv__modal-close');
      this.backdrop = this.modal && this.modal.querySelector('.sub-cat-adv__modal-backdrop');
      if (!this.modal) return;
      this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }

    openYouTube(videoId, autoplay, mobileId) {
      if (!this.modal) return;
      const ap  = autoplay === 'true' || autoplay === true;
      const mobile = isMobile();
      const id = (mobile && mobileId) ? mobileId : videoId;
      const isShort = mobile && mobileId;

      const src = `https://www.youtube.com/embed/${id}?rel=0&playsinline=1${ap ? '&autoplay=1' : ''}`;

      this.content.innerHTML = `
        <div class="${isShort ? 'yt-ratio-short' : 'yt-ratio'}">
          <iframe src="${src}"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowfullscreen
            title="YouTube video"></iframe>
        </div>`;

      if (isShort) {
        this.modal.classList.add('sub-cat-adv__modal--fullscreen');

        // Request fullscreen on the MODAL BOX (contains close btn + video),
        // not on the iframe — otherwise the close button gets excluded.
        const target = this.modal; // or this.modal.querySelector('.sub-cat-adv__modal-box')
        const iframe = this.content.querySelector('iframe');

        iframe.addEventListener('load', () => {
          try {
            const req = target.requestFullscreen
              || target.webkitRequestFullscreen
              || target.mozRequestFullScreen;
            if (req) req.call(target);
          } catch (e) { /* silently ignore if blocked */ }
        }, { once: true });
      } else {
        this.modal.classList.remove('sub-cat-adv__modal--fullscreen');
      }

      this._show();
    }

    openPDF(link, mode) {
      if (!this.modal) return;
      if (mode === 'new_tab') { window.open(link, '_blank', 'noopener,noreferrer'); return; }
      if (mode === 'download') {
        const a = document.createElement('a'); a.href = link; a.download = ''; a.click(); return;
      }
      this.content.innerHTML = `<iframe class="pdf-frame" src="${link}" title="PDF document"></iframe>`;
      this._show();
    }

    _show() {
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      this.closeBtn.focus();
    }

    close() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.classList.remove('sub-cat-adv__modal--fullscreen');
      document.body.style.overflow = '';
      this.content.innerHTML = '';
      /* Exit fullscreen if active */
      try {
        const exitFs = document.exitFullscreen
          || document.webkitExitFullscreen
          || document.mozCancelFullScreen;
        if (exitFs && document.fullscreenElement) exitFs.call(document);
      } catch(e) {}
    }
  }

  /* ── Media click / keyboard triggers ────────────────────────── */
  function bindMediaTriggers(sectionEl, modal) {
    function handle(el) {
      const type = el.dataset.media || el.dataset.mediaTrigger;
      if (type === 'youtube') {
        const desktopId = el.dataset.ytDesktop || '';
        const mobileId  = el.dataset.ytMobile  || '';
        const id        = isMobile() ? (mobileId || desktopId) : (desktopId || mobileId);
        if (id) modal.openYouTube(desktopId, el.dataset.autoplay, mobileId);
      } else if (type === 'pdf') {
        if (el.dataset.pdfLink) modal.openPDF(el.dataset.pdfLink, el.dataset.pdfMode || 'popup');
      }
    }

    /* Attach directly on each media element — bypasses Swiper's event interception */
    function attachToMediaEls() {
      sectionEl.querySelectorAll('[data-media],[data-media-trigger]').forEach((el) => {
        if (el._advBound) return; /* don't double-bind */
        el._advBound = true;

        let startX = 0, startY = 0;

        el.addEventListener('pointerdown', (e) => {
          startX = e.clientX;
          startY = e.clientY;
        }, { passive: true });

        el.addEventListener('pointerup', (e) => {
          const dx = Math.abs(e.clientX - startX);
          const dy = Math.abs(e.clientY - startY);
          /* Only fire if pointer didn't move (i.e. it's a tap/click not a drag) */
          if (dx < 8 && dy < 8) {
            e.stopPropagation();
            handle(el);
          }
        });

        /* Keyboard */
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handle(el);
          }
        });
      });
    }

    /* Run now and after Swiper may clone/reinit slides */
    attachToMediaEls();
    /* Re-attach after a tick in case Swiper hasn't rendered slides yet */
    setTimeout(attachToMediaEls, 300);
  }

  /* ── Sync sticky column width to match one Swiper slide ─────── */
  function syncStickyWidth(sectionEl, swiperEl) {
    const col = sectionEl.querySelector('.sub-cat-adv__sticky-col');
    if (!col) return;

    function update() {
      const slide = swiperEl.querySelector('.swiper-slide');
      if (!slide) return;
      const cardW = slide.getBoundingClientRect().width;
      const maxW  = parseFloat(col.style.maxWidth) || Infinity;
      const final = Math.min(cardW, maxW);
      if (final > 0) col.style.width = final + 'px';
    }

    update();
    const ro = new ResizeObserver(update);
    ro.observe(swiperEl);
    window.addEventListener('load', update);
  }

  /* ── Swiper arrows: show/hide based on position ─────────────── */
  function bindArrows(layout, swiper) {
    const prev = layout.querySelector('.sub-cat-adv__arrow--prev');
    const next = layout.querySelector('.sub-cat-adv__arrow--next');
    if (!prev || !next) return;

    function update() {
      prev.hidden = swiper.isBeginning;
      next.hidden = swiper.isEnd;
    }

    prev.addEventListener('click', () => { swiper.slidePrev(); });
    next.addEventListener('click', () => { swiper.slideNext(); });

    swiper.on('slideChange', update);
    swiper.on('reachBeginning', update);
    swiper.on('reachEnd', update);
    update();
  }

  /* ── Auto scroll using Swiper autoplay ──────────────────────── */
  function initAutoplay(layout, swiper) {
    const speed        = Math.max(1, parseFloat(layout.dataset.autoScrollSpeed) || 4);
    const pauseOnHover = layout.dataset.autoScrollPause !== 'false';
    /* Convert speed 1-10 into delay 5000-500ms */
    const delay = Math.round(6000 - speed * 500);

    swiper.params.autoplay = {
      delay:                delay,
      disableOnInteraction: false,
      pauseOnMouseEnter:    pauseOnHover,
    };
    swiper.autoplay.start();
  }

  /* ── Init one section ────────────────────────────────────────── */
  function initSection(sectionEl) {
    const layout   = sectionEl.querySelector('.sub-cat-adv__layout');
    if (!layout) return;

    /* The Swiper element is .sub-cat-adv__track (the scrollable slide container) */
    const swiperEl = sectionEl.querySelector('.sub-cat-adv__track');
    const dotsEl   = sectionEl.querySelector('.sub-cat-adv__dots');
    if (!swiperEl) return;

    /* Destroy any existing Swiper instance */
    if (swiperEl.swiper) swiperEl.swiper.destroy(true, true);

    const scrollMode  = layout.dataset.scrollMode || 'auto';
    const cpv         = parseInt(swiperEl.dataset.cardsPerView) || 5;
    const gap         = parseInt(swiperEl.dataset.gap) || 8;
    const modal       = new MediaModal(sectionEl);

    /* ── Swiper params ── */
    const params = {
      slidesPerView:  cpv,
      slidesPerGroup: 1,
      spaceBetween:   gap,
      grabCursor:     true,
      mousewheel:     { forceToAxis: true },
      preventClicks:            false,  /* don't swallow clicks */
      preventClicksPropagation: false,  /* let clicks reach our handlers */
      /* Pagination — Swiper manages bullet active class natively */
      pagination: dotsEl ? {
        el:        dotsEl,
        type:      'bullets',
        clickable: true,
        /* Map Swiper bullet classes → our CSS classes */
        bulletClass:       'sub-cat-adv__dot',
        bulletActiveClass: 'sub-cat-adv__dot--active',
        renderBullet: (index, className) => {
          return `<button class="${className}" aria-label="Page ${index + 1}"></button>`;
        },
      } : false,
      breakpoints: {
        0: {
          slidesPerView: Math.min(1.2, cpv),
          slidesPerGroup: 1,
        },
        576: {
          slidesPerView: Math.min(2, cpv),
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: Math.min(3, cpv),
          slidesPerGroup: 1,
        },
        990: {
          slidesPerView: Math.min(4, cpv),
          slidesPerGroup: 1,
        },
        1200: {
          slidesPerView: cpv,
          slidesPerGroup: 1,
        },
      },
      on: {
        /* Re-sync sticky width after init */
        afterInit(sw) {
          syncStickyWidth(sectionEl, swiperEl);
          bindArrows(layout, sw);
          if (scrollMode === 'auto') initAutoplay(layout, sw);
        },
      },
    };

    const swiper = new Swiper(swiperEl, params);

    /* Media popups — after Swiper init so drag detection is layered on top */
    bindMediaTriggers(sectionEl, modal);
  }

  /* ── Boot ────────────────────────────────────────────────────── */
  function initAll() {
    document.querySelectorAll('.sub-cat-adv-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify theme editor */
  document.addEventListener('shopify:section:load', (e) => {
    const sec = e.target.querySelector('.sub-cat-adv-section');
    if (sec) setTimeout(() => initSection(sec), 100);
  });

  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(initAll, 100);
  });

})();